import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import './consultantInterview.css';
import { Conversation } from '@elevenlabs/client';


import {
  Play,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
  Clock,
  Users,
  Target,
  TrendingUp,
  Building2,    
  MessageSquare,
  Video,
  Sparkles,
  Award,
  Calendar,
  CheckCircle,
  XCircle,
  HelpCircle,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Download,
  Share2,
  Settings,
  BarChart3,
  FileText,
  Lightbulb,
  Globe,
  Zap,
  Shield,
  Star,
  ArrowRight,
  Pause,
  Square,
  RotateCcw,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  Eye,
  EyeOff,
  Lock,
  Unlock,
  Wifi,
  WifiOff,
  Activity,
  Battery,
  BatteryCharging,
  Signal,
  SignalHigh,
  SignalLow,
  SignalMedium,
  SignalZero,
  BookOpen
} from 'lucide-react';

// Case study data for MetroMart
const caseDetails = `MetroMart is a large U.S. convenience store chain operating around 1,200 stores nationwide. They have built a reputation for being accessible, affordable, and reliable for quick purchases. However, their leadership team is concerned because over the past year, their net profit has declined by 25%, even though revenue has remained flat.`;

const companyOverview = `MetroMart's revenue mix comes from four categories: packaged snacks, beverages, prepared foods, and household items. Each category contributes differently to both sales and margins, and some shifts have occurred over the past year. The company operates in a competitive convenience store market with both traditional and digital competitors.`;

const accountOverview = `MetroMart has asked our consulting team to help them understand what's driving the drop in profitability and to identify opportunities to turn performance around. They need a clear analysis of root causes and actionable recommendations to improve their financial performance.`;

const caseObjectives = `Your task is to analyze what is happening inside the business, determine the root causes behind the profit decline, and recommend what MetroMart should do to improve performance. You'll be evaluated on problem structuring, hypothesis-driven thinking, data analysis, business judgment, and communication skills.`;

const recentProgress = `This is a candidate-led case interview simulation. You'll be working with Alex, a seasoned engagement manager-level consultant from a top-tier strategy consulting firm. The interview will assess your ability to break down complex business problems, generate hypotheses, analyze data effectively, and synthesize clear recommendations.`;

const interviewInstructions = `# Interview Instructions

## What to Expect
You're about to participate in a simulated Bain/BCG-style case interview. This is a candidate-led format where you'll be expected to:
- Structure the problem logically
- Ask for relevant data
- Develop hypotheses
- Perform calculations
- Provide clear recommendations

## Key Success Factors
1. **Problem Structuring**: Break down the complex business problem into manageable components
2. **Hypothesis-Driven Thinking**: Form and test hypotheses systematically
3. **Data Analysis**: Request and interpret relevant data to support your analysis
4. **Business Judgment**: Make practical, client-focused recommendations
5. **Communication**: Present your thoughts clearly and professionally

## Interview Format
- **Duration**: Approximately 30-45 minutes
- **Style**: Interactive, back-and-forth discussion
- **Your Role**: Lead the analysis and problem-solving
- **Interviewer**: Alex (engagement manager-level consultant) will guide and probe

## Tips for Success
- Take time to think before responding
- Ask clarifying questions when needed
- Structure your thoughts before diving into analysis
- Be hypothesis-driven in your approach
- Link your analysis back to business impact
- Stay focused on the core problem

## Remember
This is a simulation of a real consulting interview. Alex will behave like an actual interviewer, providing constructive feedback and pushing you to think rigorously. Take your time, stay composed, and demonstrate your problem-solving skills!`;

const videoPath = '/images/voiceConsulting.mp4'; 

const ConsultantInterview = () => {
    const navigate = useNavigate();
    const [isInfoExpanded, setIsInfoExpanded] = useState(false);
    const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);
    const [isAccountExpanded, setIsAccountExpanded] = useState(false);
    const [isObjectivesExpanded, setIsObjectivesExpanded] = useState(false);
    const [isTipExpanded, setIsTipExpanded] = useState(false);
    const [caseStarted, setCaseStarted] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('Connected');
    const [timeLeft, setTimeLeft] = useState(120);
    const [agentStatus, setAgentStatus] = useState('Active');
    const [transcript, setTranscript] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [consoleRef, setConsoleRef] = useState(null);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [isConnected, setIsConnected] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [conversation, setConversation] = useState(null);

    // Combined function to handle starting the case
    const handleBeginCase = async () => {
        setCaseStarted(true);
        await startConversation();
    };

    // Function to start the voice conversation
    const startConversation = async () => {
        const agentId = process.env.REACT_AGENT_ID || 'agent_7101k1cdrnqnenwvyg2kyh3pzpf8';
        
        if (!agentId) {
          alert("Configuration Error: Agent ID is missing. Please check your environment variables.");
          setCaseStarted(false);
          return;
        }
        
        try {
          // Get microphone access
          const stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            } 
          });
          
          // Start the ElevenLabs voice conversation
          const newConversation = await Conversation.startSession({
            agentId: agentId,
            onConnect: () => { 
              console.log('✅ Connected to ElevenLabs Voice Agent');
              setConnectionStatus('Connected'); 
              setIsConnected(true); 
            },
            onDisconnect: () => { 
              console.log('❌ Disconnected from ElevenLabs Voice Agent');
              setConnectionStatus('Disconnected'); 
              setIsConnected(false);
            },
            onError: (error) => {
              console.error('❌ ElevenLabs Error:', error);
              alert("An error occurred with the voice agent. Please check your console for details.");
            },
            onModeChange: (mode) => {
              setAgentStatus(mode.mode === 'speaking' ? 'speaking' : 'listening');
            },
            onMessage: (message) => {          
              console.log('📨 Message received:', message);
              
              // Handle different message types and build conversation history
              let messageEntry = null;

              if (message.user_transcription_event) {
                console.log('👤 User:', message.user_transcription_event.user_transcript);
                messageEntry = {
                  type: 'user',
                  role: 'user',
                  content: message.user_transcription_event.user_transcript,
                  timestamp: new Date().toISOString(),
                  source: 'user_transcription_event'
                };
              } else if (message.agent_response_event) {
                console.log('👨🏼‍💼 Agent:', message.agent_response_event.agent_response);
                messageEntry = {
                  type: 'agent',
                  role: 'assistant',
                  content: message.agent_response_event.agent_response,
                  timestamp: new Date().toISOString(),
                  source: 'agent_response_event'
                };
              } else if (message.message && message.source) {
                // Fallback for other message types
                messageEntry = {
                  type: message.source === 'user' ? 'user' : 'agent',
                  role: message.source === 'user' ? 'user' : 'assistant',
                  content: message.message,
                  timestamp: new Date().toISOString(),
                  source: message.source
                };
              }

                              // Add message to conversation history if we have a valid entry
                if (messageEntry) {
                  console.log('✅ Adding message to conversation history:', messageEntry);
                  setConversationHistory(prev => {
                    const newHistory = [...prev, messageEntry];
                    console.log('📝 Updated conversation history:', newHistory);
                    return newHistory;
                  });
                  
                  // Update transcript for display - include both user and AI messages
                  setTranscript(prev => {
                    const rolePrefix = messageEntry.type === 'user' ? '👤 You: ' : '🤖 Alex: ';
                    const newTranscript = `${prev}\n${rolePrefix}${messageEntry.content}`.trim();
                    console.log('📝 Updated transcript:', newTranscript);
                    return newTranscript;
                  });
                  
                  // Auto-scroll to bottom when new message arrives
                  setTimeout(() => {
                    const transcriptContent = document.querySelector('.transcript-content');
                    if (transcriptContent) {
                      transcriptContent.scrollTop = transcriptContent.scrollHeight;
                    }
                  }, 100);
                } else {
                  console.log('❌ No valid message entry created for:', message);
                }
            }
          });
          
          // Store the conversation object
          setConversation(newConversation);
          
          console.log('✅ Voice conversation started with agent:', agentId);
          console.log('✅ Added welcome message to conversation history');
          
        } catch (error) {
          console.error('❌ Failed to start conversation:', error);
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
             alert("Microphone access was denied. Please allow microphone access in your browser settings to begin the case study.");
          } else if (error.name === 'NotSupportedError') {
             alert("Your browser may not support the required audio features. Please try using Chrome or Firefox.");
          } else {
             alert("An error occurred while starting the simulation. Please check your console for details.");
          }
          setCaseStarted(false);
        }
    };

    // Function to stop the conversation and end the case
    const stopConversation = async () => {
        if (conversation) {
            console.log('=== STOPPING CONVERSATION ===');
            try {
                // End the ElevenLabs voice session
                await conversation.endSession();
                console.log('✅ Voice session ended successfully');
            } catch (error) {
                console.error('❌ Error ending voice session:', error);
            }
            setConversation(null);
            setIsConnected(false);
        }
        
        // Reset case state
        setCaseStarted(false);
        setConversationHistory([]);
        setTranscript('');
        setTimeLeft(120);
        setAgentStatus('Active');
        setConnectionStatus('Connected');
        
        // Show completion message
        alert('Case study completed successfully! Thank you for participating.');
    };

    // Function to copy transcript
    const copyTranscript = () => {
        const transcriptText = conversationHistory.map(entry => 
            `${entry.type === 'user' ? 'You: ' : 'Alex: '}${entry.content}`
        ).join('\n');
        
        navigator.clipboard.writeText(transcriptText).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }).catch(err => console.error('Failed to copy transcript:', err));
    };

    // Function to clear transcript
    const handleClearTranscript = () => {
        setTranscript('');
        setConversationHistory([]);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const updateTimeLeft = () => {
        setTimeLeft(timeLeft - 1);
    };

    const updateConnectionStatus = () => {
        setConnectionStatus(connectionStatus === 'Connected' ? 'Disconnected' : 'Connected');
    };

    const updateAgentStatus = () => {
        setAgentStatus(agentStatus === 'Active' ? 'Inactive' : 'Active');
    };

    const toggleRecording = () => {
        if (conversation) {
            if (isRecording) {
                // Stop recording
                conversation.stopRecording();
                setIsRecording(false);
            } else {
                // Start recording
                conversation.startRecording();
                setIsRecording(true);
            }
        } else {
            setIsRecording(!isRecording);
        }
    };

    const toggleMute = () => {
        setIsMuted(!isMuted);
    };

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    return (
        <div className="consultant-interview">
            {/* Navbar */}
            <Navbar />

            <div className="flex-1 p-6 overflow-y-auto">
                <div className="max-w-7xl mx-auto">
                    {/* Header Section */}
                    <div className="interview-header">
                        <div className="interview-eyebrow">
                            <Sparkles className="w-5 h-5" />
                            <span className="text-sm font-medium">Management Consulting Case Interview</span>
                        </div>
                        <h1 className="interview-title">
                            MetroMart Profitability Case Study
                        </h1>
                        <p className="interview-description">
                            Practice your case interview skills with this MetroMart case study. Watch the video below for context, 
                            review the case details, and when ready, begin the interactive simulation with Alex, your interviewer.
                        </p>
                    </div>

                    {/* Video Section */}
                    <div className="video-container">
                        <div className="video-player">
                            <video 
                                src={videoPath} 
                                controls 
                                controlsList="nodownload"
                                className="w-full h-full object-cover"
                            />
                            <div className="video-controls">
                                <button
                                    className="icon-btn secondary"
                                    onClick={toggleFullscreen}
                                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                >
                                    {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                                </button>
                            </div>
                        </div>
                    </div>

                                        {/* Information Sections Grid */}
                    <div className="info-grid">
                    </div>

                    {/* Interview Instructions Section */}
                    <div className="instructions-container">
                        <div className="instructions-header">
                            <div className="instructions-icon">
                                <BookOpen size={24} />
                            </div>
                            <h2 className="instructions-title">Interview Instructions</h2>
                        </div>
                        
                        {/* What to Expect - Full Width */}
                        <div className="instruction-section main-expectation">
                            <h3 className="section-title">What to Expect</h3>
                            <p className="section-description">
                                You're about to participate in a simulated Bain/BCG-style case interview. 
                                This is a candidate-led format where you'll be expected to structure problems, 
                                develop hypotheses, analyze data, and provide recommendations.
                            </p>
                        </div>

                        {/* Three Column Layout for Key Information */}
                        <div className="instructions-three-column">
                            <div className="instruction-column">
                                <div className="column-header">
                                    <div className="column-icon success">
                                        <CheckCircle size={20} />
                                    </div>
                                    <h4>Key Success Factors</h4>
                                </div>
                                <ul className="column-list">
                                    <li>Problem Structuring & Logic</li>
                                    <li>Hypothesis-Driven Thinking</li>
                                    <li>Data & Analytical Rigor</li>
                                    <li>Business Judgment & Practicality</li>
                                    <li>Communication & Executive Presence</li>
                                </ul>
                            </div>

                            <div className="instruction-column">
                                <div className="column-header">
                                    <div className="column-icon info">
                                        <Clock size={20} />
                                    </div>
                                    <h4>Interview Format</h4>
                                </div>
                                <ul className="column-list">
                                    <li><strong>Duration:</strong> 5-10 minutes</li>
                                    <li><strong>Style:</strong> Interactive discussion</li>
                                    <li><strong>Your Role:</strong> Lead the analysis</li>
                                    <li><strong>Interviewer:</strong> Alex (engagement manager)</li>
                                </ul>
                            </div>

                            <div className="instruction-column">
                                <div className="column-header">
                                    <div className="column-icon primary">
                                        <Lightbulb size={20} />
                                    </div>
                                    <h4>Tips for Success</h4>
                                </div>
                                <ul className="column-list">
                                    <li>Take time to think before responding</li>
                                    <li>Ask clarifying questions when needed</li>
                                    <li>Structure your thoughts systematically</li>
                                    <li>Be hypothesis-driven in your approach</li>
                                    <li>Link analysis back to business impact</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Begin Case Button */}
                    {!caseStarted && (
                        <div className="begin-case-container">
                            <button
                                className="begin-case-btn"
                                onClick={handleBeginCase}
                            >
                                <Play size={20} />
                                Begin Case Study
                            </button>
                        </div>
                    )}

                    {/* Case Console Section */}
                    {caseStarted && (
                        <div className="case-console">
                            <div className="p-6">
                                {/* Console Header */}
                                <div className="console-header">
                                    <div className="console-title-section">
                                        <div className="console-icon">
                                            <MessageSquare size={24} />
                                        </div>
                                        <div>
                                            <h2 className="console-title">
                                                Case Interview Simulation
                                            </h2>
                                            <p className="console-subtitle">
                                                Interactive consulting case interview with Alex
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {/* Status Indicators */}
                                    <div className="status-indicators">
                                        <div className="status-item">
                                            <div className={`status-dot ${connectionStatus === 'Connected' ? 'connected' : 'disconnected'}`}></div>
                                            <span className={`status-badge ${connectionStatus === 'Connected' ? 'success' : 'danger'}`}>
                                                {connectionStatus}
                                            </span>
                                        </div>
                                        
                                        <div className="status-item">
                                            <div className={`status-dot ${agentStatus === 'Active' ? 'active' : 'inactive'}`}></div>
                                            <span className={`status-badge ${agentStatus === 'Active' ? 'info' : 'default'}`}>
                                                {agentStatus}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Control Bar */}
                                <div className="control-bar">
                                    <div className="control-left">
                                        <button
                                            className={`btn ${isRecording ? 'btn-danger' : 'btn-success'} btn-sm`}
                                            onClick={toggleRecording}
                                        >
                                            {isRecording ? <Square size={16} /> : <Mic size={16} />}
                                            {isRecording ? "Stop Recording" : "Start Recording"}
                                        </button>
                                        
                                        <button
                                            className={`icon-btn ${isMuted ? 'danger' : 'secondary'}`}
                                            onClick={toggleMute}
                                            title={isMuted ? "Unmute" : "Mute"}
                                        >
                                            {isMuted ? <MicOff size={16} /> : <Mic size={16} />}
                                        </button>
                                        
                                        <button
                                            className="icon-btn secondary"
                                            onClick={() => setTimeLeft(120)}
                                            title="Reset Timer"
                                        >
                                            <RotateCcw size={16} />
                                        </button>
                                        
                                    </div>

                                    <div className="control-right">
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={stopConversation}
                                        >
                                            <PhoneOff size={16} />
                                            End Simulation
                                        </button>
                                    </div>
                                </div>

                                {/* Transcript Area */}
                                <div className="transcript-container">
                                    <div
                                        className="transcript-header"
                                        onClick={() => setShowTranscript(!showTranscript)}
                                    >
                                        <div className="transcript-title-section">
                                            <div className="transcript-icon">
                                                <FileText size={16} />
                                            </div>
                                            <h3 className="transcript-title">
                                                Conversation Transcript
                                            </h3>
                                            {conversationHistory.length > 0 && (
                                                <span className="status-badge info">
                                                    {conversationHistory.length} messages
                                                </span>
                                            )}
                                        </div>
                                        
                                        <div className="transcript-controls">
                                            {conversationHistory.length > 0 && (
                                                <>
                                                    <button
                                                        className="icon-btn secondary"
                                                        onClick={copyTranscript}
                                                        title={isCopied ? "Copied!" : "Copy Transcript"}
                                                    >
                                                        {isCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                                                    </button>
                                                    <button
                                                        className="icon-btn danger"
                                                        onClick={handleClearTranscript}
                                                        title="Clear Transcript"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </>
                                            )}
                                            
                                            {conversationId && (
                                                <button
                                                    className="icon-btn accent"
                                                    onClick={() => console.log("Conversation ID:", conversationId)}
                                                    title="View Analytics"
                                                >
                                                    <BarChart3 size={16} />
                                                </button>
                                            )}
                                            
                                            <button
                                                className="icon-btn secondary"
                                                title={showTranscript ? "Hide Transcript" : "Show Transcript"}
                                            >
                                                {showTranscript ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        </div>
                                    </div>

                                    {showTranscript && (
                                        <div className="transcript-content" id="transcript-content">
                                            {/* Show structured conversation history as the primary display */}
                                            {conversationHistory.length > 0 && (
                                                <div className="conversation-history-section">
                                                    <div className="conversation-entries">
                                                        {conversationHistory.map((entry, index) => (
                                                            <div
                                                                key={index}
                                                                className={`conversation-entry ${entry.type}`}
                                                            >
                                                                <div className="entry-header">
                                                                    <span className={`entry-role ${entry.type}`}>
                                                                        {entry.type === "user" ? "👤 You" : "🤖 Alex"}
                                                                    </span>
                                                                    <span className="entry-timestamp">
                                                                        {new Date(entry.timestamp).toLocaleTimeString(
                                                                            [],
                                                                            { hour: "2-digit", minute: "2-digit" }
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                <div className="entry-content">{entry.content}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Show a message when no conversation history yet */}
                                            {conversationHistory.length === 0 && (
                                                <div className="no-conversation-message">
                                                    <p>The conversation will appear here as you speak...</p>
                                                </div>
                                            )}
                                            
                                            {/* Scroll to bottom button */}
                                            <button
                                                className="scroll-to-bottom-btn"
                                                onClick={() => {
                                                    const transcriptContent = document.getElementById('transcript-content');
                                                    if (transcriptContent) {
                                                        transcriptContent.scrollTop = transcriptContent.scrollHeight;
                                                    }
                                                }}
                                                title="Scroll to latest message"
                                            >
                                                <ChevronDown size={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConsultantInterview;